import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
interface CardDisplaySettings {
    showVersion: boolean;
    showSchedule: boolean;
    showSizeAndWeight: boolean;
    showBadge: boolean;
    showStatus: boolean;
}

interface CardSettingsContextType {
    selectedSettings: (string | number)[];
    setSelectedSettings: (values: (string | number)[]) => void;
    toggleSetting: (value: string | number) => void;
    isSettingSelected: (value: string | number) => boolean;
    displaySettings: CardDisplaySettings;
    toggleDisplaySetting: (setting: keyof CardDisplaySettings) => void;
    isDisplaySettingSelected: (setting: keyof CardDisplaySettings) => boolean;
}

const CardSettingsContext = createContext<CardSettingsContextType | undefined>(undefined);

export const useCardSettings = () => {
    const context = useContext(CardSettingsContext);

    if (!context) {
        throw new Error('useCardSettings must be used within a CardSettingsProvider');
    }

    return context;
};

interface CardSettingsProviderProps {
    children: ReactNode;
    initialSettings?: (string | number)[];
    initialDisplaySettings?: Partial<CardDisplaySettings>;
}

const CardSettingsProvider: React.FC<CardSettingsProviderProps> = ({
    children,
    initialSettings = [],
    initialDisplaySettings = {}
}) => {
    const [selectedSettings, setSelectedSettings] = useState<(string | number)[]>(initialSettings);
    const [displaySettings, setDisplaySettings] = useState<CardDisplaySettings>({
        showVersion: true,
        showSchedule: true,
        showSizeAndWeight: true,
        showBadge: true,
        showStatus: true,
        ...initialDisplaySettings
    });

    const toggleSetting = useCallback((value: string | number) => {
        setSelectedSettings(prev =>
            prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value]
        );
    }, []);

    const toggleDisplaySetting = (setting: keyof CardDisplaySettings) => {
        setDisplaySettings(prev => ({
            ...prev,
            [setting]: !prev[setting]
        }));
    };

    const isSettingSelected = useCallback((value: string | number) => {
        return selectedSettings.includes(value);
    }, [selectedSettings]);

    const isDisplaySettingSelected = useCallback((setting: keyof CardDisplaySettings) => {
        return displaySettings[setting] === true;
    }, [displaySettings]);

    return (
        <CardSettingsContext.Provider value={{
            selectedSettings,
            setSelectedSettings,
            toggleSetting,
            isSettingSelected,
            displaySettings,
            toggleDisplaySetting,
            isDisplaySettingSelected
        }}>
            {children}
        </CardSettingsContext.Provider>
    );
};

export default CardSettingsProvider;
